<script setup lang="ts">
/**
 * Loading, failed, or the thing itself.
 *
 * A FAILURE IS NEVER AN EMPTY TABLE. The whole reason this component exists is
 * that "the server did not answer" and "the venue sold nothing" render
 * identically if you let a failed request fall through to an empty list, and
 * one of those is a fact about the business.
 *
 * `role="alert"` on the failure, `role="status"` on the wait: a manager using a
 * screen reader should be interrupted by the first and not by the second.
 */

import { GfSkeleton } from "@gf/ui";
import type { Loaded } from "../load";

defineProps<{
  state: Loaded<unknown>;
  labels: { loading: string; failed: string; retry: string; unreachable: string };
}>();

defineEmits<{ retry: [] }>();
</script>

<template>
  <!--
    A SHAPE, not a sentence.

    This was a grey paragraph reading "Načítám…". It told a manager that
    something was happening and nothing about what, and when the answer arrived
    the page jumped from one line of text to a full table. A block the size of
    what is coming says both, and holds the space while it comes.

    The label is still here and still in the live region: `GfSkeleton` is
    `aria-hidden` on purpose, so a screen reader hears the sentence and not nine
    blank rectangles.
  -->
  <div v-if="state.status === 'loading'" class="load load--waiting" role="status">
    <span class="gf-sr">{{ labels.loading }}</span>
    <GfSkeleton :lines="5" height="1.4em" />
  </div>

  <div v-else-if="state.status === 'failed'" class="load load--failed" role="alert">
    <strong>{{ labels.failed }}</strong>
    <p class="load__why">
      {{ state.kind === "unreachable" ? labels.unreachable : state.detail }}
    </p>
    <button type="button" class="load__retry" @click="$emit('retry')">{{ labels.retry }}</button>
  </div>

  <slot v-else />
</template>

<style scoped>
.load {
  margin: 0;
  padding: var(--gf-space-4) var(--gf-space-5);
  border-radius: var(--gf-radius-lg);
  font-size: var(--gf-text-base);
}

.load--waiting {
  padding: 0;
  color: var(--gf-color-text-muted);
  background: transparent;
}

/*
  A failure is a tinted callout, not a red-ruled box: the tint says "something
  is wrong", the words say what, and the retry is an ordinary button — it is
  not the thing the screen exists for.
*/
.load--failed {
  background: var(--gf-color-danger-surface);
  color: var(--gf-color-text);
}

.load__why {
  margin: var(--gf-space-1) 0 0;
  color: var(--gf-color-text-muted);
  line-height: var(--gf-leading-normal);
}

.load__retry {
  margin-top: var(--gf-space-3);
  min-height: var(--gf-target-min);
  padding: 0 var(--gf-space-4);
  font: inherit;
  font-weight: var(--gf-weight-medium);
  color: var(--gf-color-text);
  background: var(--gf-color-surface);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-md);
  box-shadow: var(--gf-shadow-sm);
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

.load__retry:hover {
  background: var(--gf-color-surface-hover);
}

.load__retry:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}
</style>
